import { Link } from "react-router-dom";
import Footer from "../components/Footer";
import PublicNav from "../components/PublicNav";

/**
 * Public roadmap.
 *
 * Mirrors ROADMAP.md, with the pre-career track deliberately omitted. School
 * GTM, cohort mechanics, the counsellor dashboard and the departure ritual are
 * horizon work and stay internal until agencies are established on the
 * platform. If you edit ROADMAP.md, edit this too, there is no shared source.
 */
const roadmap = [
  {
    stage: "Phase 1",
    title: "Foundation",
    subtitle: "The loop, working",
    status: "done",
    description:
      "Live today. Agencies create positions and pipelines, share shortlists with clients, and push real status updates to candidates. Candidates see honest status instead of silence.",
    columns: {
      agencies: [
        "Position creation and pipelines",
        "Client shortlist sharing",
        "Bulk status push to candidates",
        "Agency ownership of the process",
      ],
      employers: [
        "Connect tokens and resume slugs",
        "Curated client view",
        "Per-candidate feedback",
        "Real applications, not noise",
      ],
      seekers: [
        "Application tracking dashboard",
        "Verified status updates",
        "Honest status, no fake signal",
        '"Via Joborigo" badge on applications',
      ],
      platform: [
        "EU-based infrastructure",
        "Email verification and auth",
        "Production observability",
        "Paddle Merchant of Record billing",
        "Blog, About and public roadmap",
      ],
    },
  },
  {
    stage: "Phase 2",
    title: "Active",
    subtitle: "Depth on every side",
    status: "current",
    description:
      "Shipping now. Joborigo is free for job seekers, forever. Agency and employer plans fund the platform, and this phase is where their pipeline depth matures.",
    columns: {
      agencies: [
        "Multi-recruiter plans",
        "Per-client dashboards",
        "Referral attribution",
        "Pipeline view depth",
      ],
      employers: [
        "Pipeline status push to all connected candidates",
        "Employer dashboard",
      ],
      seekers: [
        "Free for job seekers, forever",
        "Ghost funnel visualisation",
        "LinkedIn profile import for the resume builder",
        "Connected employers and agencies table",
        "Cover letter impact tracking",
      ],
      platform: [
        "Domain verification",
        "Peer endorsement",
        "Founding Partner program",
        "Signup geography capture",
      ],
    },
  },
  {
    stage: "Phase 3",
    title: "Intelligence",
    subtitle: "Harder questions, answered",
    status: "upcoming",
    description:
      "The data already in the platform starts answering harder questions. Career trajectory, capability matching and application strength, visible to anyone who chooses the capability-mode view.",
    columns: {
      agencies: [
        "Capability-mode toggle",
        "Candidate Capability Readiness scoring",
        "Capability-map search",
        "Sourcing operations toolkit",
      ],
      employers: [
        "Capability-mode shortlist views",
        "Application strength scoring",
      ],
      seekers: [
        "Career trajectory analysis",
        "Skills gap analysis",
        "Salary negotiation intelligence",
        "Personal ghost log",
      ],
      platform: [
        "Per-position capability extraction",
        "Local language support (Swedish, Norwegian, Dutch)",
        "Labour Market Index v1",
      ],
    },
  },
  {
    stage: "Phase 4",
    title: "Verified",
    subtitle: "Identity that travels",
    status: "future",
    description:
      "Work history becomes real, portable and provable. The Verified Profile belongs to the candidate, not to Joborigo, and they take it with them when they leave.",
    columns: {
      agencies: ["Verified candidate credentials", "Early talent pool access"],
      employers: [
        "Early talent pipeline access",
        "Graduate recruitment integration",
      ],
      seekers: [
        "Joborigo Verified Profile",
        "Portable career identity",
        "Peer endorsement at scale",
        "Cryptographic work history provenance",
      ],
      platform: ["Warm intro mapping", "Labour Market Index published"],
    },
  },
  {
    stage: "Phase 5",
    title: "Platform",
    subtitle: "Open infrastructure",
    status: "future",
    description:
      "Joborigo opens to the ecosystem. Third parties build on the platform, and the Labour Market Index becomes a public reference for how hiring actually behaves.",
    columns: {
      agencies: [
        "ATS integrations",
        "Capability-mode toggle at full depth",
        "Early-career talent pool access",
      ],
      employers: [
        "Verified candidate credentials at scale",
        "Team job searching",
      ],
      seekers: [
        "Company ghost lookup",
        "Ghost leaderboard",
        "Browser extension for application capture",
      ],
      platform: [
        "Public API",
        "Plugin registry and marketplace",
        "Labour Market Index, full edition",
      ],
    },
  },
  {
    stage: "Phase 6",
    title: "Companion",
    subtitle: "A career, not a job search",
    status: "future",
    description:
      "Joborigo stays quietly alongside a working life rather than showing up only when someone is looking. Verified identity that started honest and stayed honest.",
    columns: {
      agencies: ["Verified talent at every career stage"],
      employers: ["Verified career identity across the workforce"],
      seekers: [
        "A career identity that travels with you",
        "Background guidance, surfaced only when it matters",
      ],
      platform: ["Joborigo as infrastructure for professional identity"],
    },
  },
];

const statusStyles = {
  done: "bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400",
  current:
    "bg-brand-violet-tint text-brand-violet dark:bg-brand-violet/20 dark:text-brand-violet-on-dark",
  upcoming:
    "bg-amber-50 text-amber-600 dark:bg-amber-900/20 dark:text-amber-400",
  future: "bg-gray-100 text-gray-500 dark:bg-white/10 dark:text-gray-400",
};

const statusLabel = {
  done: "Launched",
  current: "Active",
  upcoming: "Building",
  future: "Planned",
};

const cardBorders = {
  done: "border-green-100 dark:border-green-900/30 bg-green-50/30 dark:bg-green-900/5",
  current:
    "border-brand-violet/30 dark:border-brand-violet-on-dark/30 bg-brand-violet-tint/50 dark:bg-brand-violet/5",
  upcoming:
    "border-amber-100 dark:border-amber-900/30 bg-amber-50/30 dark:bg-amber-900/5",
  future:
    "border-gray-100 dark:border-white/10 bg-gray-50 dark:bg-brand-dark-surface",
};

const dotColors = {
  done: "bg-green-500",
  current: "bg-brand-violet dark:bg-brand-violet-on-dark",
  upcoming: "bg-amber-500",
  future: "bg-gray-400 dark:bg-white/30",
};

function ColumnList({ heading, items, status }) {
  return (
    <div>
      <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-3">
        {heading}
      </p>
      {items && items.length > 0 ? (
        <ul className="flex flex-col gap-1.5">
          {items.map((item) => (
            <li
              key={item}
              className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-300"
            >
              <span
                className={`w-1.5 h-1.5 rounded-full flex-shrink-0 mt-1.5 ${dotColors[status]}`}
              />
              <span className="leading-snug">{item}</span>
            </li>
          ))}
        </ul>
      ) : (
        // Words rather than a dash, so a scanning reader is not left guessing
        <p className="text-sm text-gray-400 dark:text-gray-600">
          Nothing planned this phase
        </p>
      )}
    </div>
  );
}

export default function Roadmap() {
  return (
    <div className="min-h-screen flex flex-col bg-white dark:bg-brand-dark-bg text-gray-900 dark:text-gray-100">
      <PublicNav />
      <main className="flex-1 max-w-6xl mx-auto px-6 py-16 w-full">
        {/* Header */}
        <section className="mb-12 max-w-3xl">
          <p className="text-sm font-medium text-brand-violet dark:text-brand-violet-on-dark mb-3 uppercase tracking-widest">
            Roadmap
          </p>
          <h1 className="text-4xl font-extrabold mb-6 leading-tight">
            What's live,
            <br />
            what's coming.
          </h1>
          <p className="text-gray-500 dark:text-gray-400 text-lg leading-relaxed mb-4">
            Foundation is live and in use by early customers. Active is shipping
            now. Everything after that is the direction we are committed to
            rather than a delivery date, and we update this page as work ships.
          </p>
          <p className="text-gray-500 dark:text-gray-400 text-lg leading-relaxed">
            Each phase ships across all three sides of the loop, plus the
            platform underneath. Joborigo's wedge is three-sided transparency,
            and the roadmap reflects that at every step.
          </p>
        </section>

        {/* Phases */}
        <section className="flex flex-col gap-8 mb-16">
          {roadmap.map((stage) => (
            <div
              key={stage.stage}
              className={`rounded-2xl border p-6 sm:p-8 ${cardBorders[stage.status]}`}
            >
              {/* Phase header */}
              <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
                <div>
                  <span className="text-xs font-medium text-gray-400 uppercase tracking-widest">
                    {stage.stage}
                  </span>
                  <h2 className="text-xl sm:text-2xl font-bold mt-1">
                    {stage.title}
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    {stage.subtitle}
                  </p>
                </div>
                <span
                  className={`text-xs font-medium px-3 py-1 rounded-full flex-shrink-0 ${statusStyles[stage.status]}`}
                >
                  {statusLabel[stage.status]}
                </span>
              </div>

              <p className="text-gray-500 dark:text-gray-400 text-sm leading-relaxed mb-6 max-w-3xl">
                {stage.description}
              </p>

              {/* Four columns: stack on mobile, 2-col on tablet, 4-col on desktop */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
                <ColumnList
                  heading="Agencies"
                  items={stage.columns.agencies}
                  status={stage.status}
                />
                <ColumnList
                  heading="Employers"
                  items={stage.columns.employers}
                  status={stage.status}
                />
                <ColumnList
                  heading="Job seekers"
                  items={stage.columns.seekers}
                  status={stage.status}
                />
                <ColumnList
                  heading="Platform"
                  items={stage.columns.platform}
                  status={stage.status}
                />
              </div>
            </div>
          ))}
        </section>

        {/* Honest closing */}
        <section className="mb-12 max-w-3xl">
          <p className="text-sm font-medium text-brand-violet dark:text-brand-violet-on-dark mb-3 uppercase tracking-widest">
            The honest part
          </p>
          <h2 className="text-2xl font-bold mb-4">
            Roadmaps are commitments, not certainties.
          </h2>
          <p className="text-gray-500 dark:text-gray-400 text-base leading-relaxed mb-4">
            Joborigo is built by a solo founder alongside a full-time role.
            Foundation and Active timelines are reliable. The later phases
            reflect the strategic direction we are committed to, but specific
            feature ordering may shift as customer feedback compounds.
          </p>
          <p className="text-gray-500 dark:text-gray-400 text-base leading-relaxed">
            If you are an agency considering Joborigo and the roadmap timing
            matters to you, the best path is to reach out. We make scope
            decisions in dialogue with early customers, not in isolation.
          </p>
        </section>

        {/* CTA */}
        <div className="rounded-2xl bg-brand-violet p-px max-w-2xl mx-auto">
          <div className="rounded-2xl bg-white dark:bg-brand-dark-surface px-8 py-10 text-center">
            <h2 className="text-2xl font-bold mb-2">
              Want to shape what comes next?
            </h2>
            <p className="text-gray-500 dark:text-gray-400 mb-6">
              The Founding Partner program is open. Direct roadmap influence,
              hands-on onboarding.
            </p>
            <Link
              to="/register"
              className="inline-block bg-brand-violet text-white px-8 py-3 rounded-lg font-medium hover:opacity-90 transition"
            >
              Get started
            </Link>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
